import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	FormHelperText,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	Switch,
	TextField,
	Typography,
	Unstable_Grid2 as Grid
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';

import { isRootAdmin, useAuthContext } from '../../contexts/auth-context';
import {
	CONFIGURE_DELIVERY_LOCATION,
	DELIVERY_LOCATION_CONFIG,
	SET_DELIVERY_LOCATION_ENABLED
} from '../../gql/queries';
import { FleetModel } from '../../gql/graphql';
import DeliveryPayments from './delivery.payments';

interface DeliverySettingsProps {
	locationId: string;
	locationName: string;
	open: boolean;
	onClose: () => void;
	/** Called after a successful save so the list can show the new state. */
	onSaved?: () => void;
}

interface FormState {
	latitude: string;
	longitude: string;
	deliveryRadiusKm: string;
	baseFee: string;
	perKmRate: string;
	minFee: string;
	driverPerKmRate: string;
	fleetModel: FleetModel;
	isDeliveryEnabled: boolean;
	acceptsCashOnDelivery: boolean;
}

/** What a location starts with when delivery has never been set up for it. */
const DEFAULTS: FormState = {
	latitude: '',
	longitude: '',
	deliveryRadiusKm: '5',
	baseFee: '20',
	perKmRate: '5',
	minFee: '30',
	driverPerKmRate: '4',
	fleetModel: FleetModel.OwnFleet,
	isDeliveryEnabled: false,
	acceptsCashOnDelivery: true
};

/** The distance the worked example below is priced at. */
const SAMPLE_DISTANCE_KM = 3;

const number = (value: string): number => Number(value.trim());
const isMoney = (value: string): boolean => value.trim() !== '' && Number.isFinite(number(value)) && number(value) >= 0;

/**
 * Delivery settings for one location.
 *
 * Two things live here and they are not the same decision, which is why the
 * API splits them too: the configuration (where the store is, how far it
 * delivers, what it charges) needs `location.write`, while switching delivery
 * on for customers needs `delivery.capability` — a Super Admin. So the switch
 * is shown to everyone and only a Super Admin can move it.
 *
 * The two per-km rates are deliberately far apart on the form and captioned
 * with who pays whom. They are different numbers with different purposes — one
 * is revenue, the other is cost — and a form that let them read as one field
 * would be how they silently became equal.
 */
export default function DeliverySettings({
	locationId,
	locationName,
	open,
	onClose,
	onSaved
}: DeliverySettingsProps) {
	const { jwtDecoded } = useAuthContext();
	const canEnable = isRootAdmin(jwtDecoded);

	const [form, setForm] = useState<FormState>(DEFAULTS);
	const [locating, setLocating] = useState(false);

	const { data, loading: loadingConfig, refetch: refetchConfig } = useQuery(DELIVERY_LOCATION_CONFIG, {
		variables: { locationId },
		skip: !open || !locationId,
		fetchPolicy: 'network-only'
	});

	const config = data?.deliveryLocationConfig;
	const isConfigured = !!config;

	useEffect(() => {
		if (!open) {
			return;
		}
		if (config) {
			setForm({
				latitude: String(config.latitude),
				longitude: String(config.longitude),
				deliveryRadiusKm: String(config.deliveryRadiusKm),
				baseFee: String(config.baseFee),
				perKmRate: String(config.perKmRate),
				minFee: String(config.minFee),
				driverPerKmRate: String(config.driverPerKmRate),
				fleetModel: config.fleetModel,
				isDeliveryEnabled: config.isDeliveryEnabled,
				acceptsCashOnDelivery: config.acceptsCashOnDelivery ?? true
			});
		} else if (!loadingConfig) {
			setForm(DEFAULTS);
		}
	}, [config, loadingConfig, open]);

	const [configure, { loading: saving }] = useMutation(CONFIGURE_DELIVERY_LOCATION);
	const [setEnabled, { loading: switching }] = useMutation(SET_DELIVERY_LOCATION_ENABLED);

	const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		setForm((prev) => ({ ...prev, [name]: value }));
	}, []);

	/**
	 * Fills the coordinates from the browser, for someone setting this up while
	 * sitting in the shop. Everywhere else they come from a map: right-click the
	 * spot in Google Maps and the first two numbers are latitude and longitude.
	 */
	const useThisDeviceLocation = useCallback(() => {
		if (!navigator.geolocation) {
			toast.error('This browser cannot read a location. Enter the coordinates by hand.');
			return;
		}
		setLocating(true);
		navigator.geolocation.getCurrentPosition(
			(position) => {
				setForm((prev) => ({
					...prev,
					latitude: position.coords.latitude.toFixed(6),
					longitude: position.coords.longitude.toFixed(6)
				}));
				setLocating(false);
				toast.success('Coordinates filled from this device.');
			},
			() => {
				setLocating(false);
				toast.error('Could not read this device’s location. Enter the coordinates by hand.');
			},
			{ enableHighAccuracy: true, timeout: 10000 }
		);
	}, []);

	/** Mirrors the API's own validation, so a mistake is caught before the round trip. */
	const error = useMemo((): string | null => {
		const lat = number(form.latitude);
		const lng = number(form.longitude);
		if (form.latitude.trim() === '' || !Number.isFinite(lat) || lat < -90 || lat > 90) {
			return 'Latitude must be a number between -90 and 90.';
		}
		if (form.longitude.trim() === '' || !Number.isFinite(lng) || lng < -180 || lng > 180) {
			return 'Longitude must be a number between -180 and 180.';
		}
		if (lat === 0 && lng === 0) {
			return 'Those coordinates are in the Atlantic. Enter the store’s real position.';
		}
		if (!Number.isFinite(number(form.deliveryRadiusKm)) || number(form.deliveryRadiusKm) <= 0) {
			return 'The delivery radius must be greater than zero.';
		}
		if (![form.baseFee, form.perKmRate, form.minFee, form.driverPerKmRate].every(isMoney)) {
			return 'Fees and rates must be zero or more.';
		}
		return null;
	}, [form]);

	/** The same arithmetic the API uses: ceil(base + perKm x distance), floored at the minimum. */
	const example = useMemo(() => {
		if (error) {
			return null;
		}
		const charged = Math.max(
			Math.ceil(number(form.baseFee) + number(form.perKmRate) * SAMPLE_DISTANCE_KM),
			number(form.minFee)
		);
		const paid = number(form.driverPerKmRate) * SAMPLE_DISTANCE_KM;
		return { charged, paid, margin: charged - paid };
	}, [error, form]);

	const handleSubmit = useCallback(
		async (event: React.FormEvent) => {
			event.preventDefault();
			if (error) {
				toast.error(error);
				return;
			}

			try {
				await configure({
					variables: {
						input: {
							locationId,
							latitude: number(form.latitude),
							longitude: number(form.longitude),
							deliveryRadiusKm: number(form.deliveryRadiusKm),
							baseFee: number(form.baseFee),
							perKmRate: number(form.perKmRate),
							minFee: number(form.minFee),
							driverPerKmRate: number(form.driverPerKmRate),
							fleetModel: form.fleetModel,
							// Only a Super Admin may change this, so for everyone else the
							// stored value is sent back untouched rather than the form's.
							isDeliveryEnabled: canEnable ? form.isDeliveryEnabled : config?.isDeliveryEnabled ?? false,
							acceptsCashOnDelivery: form.acceptsCashOnDelivery
						}
					}
				});
				toast.success(`Delivery settings saved for ${locationName}.`);
				onSaved?.();
				onClose();
			} catch (saveError: any) {
				toast.error(`Could not save: ${saveError.message}`);
			}
		},
		[canEnable, config, configure, error, form, locationId, locationName, onClose, onSaved]
	);

	/**
	 * The switch saves on its own rather than waiting for the form, because
	 * turning delivery off is something you do in a hurry — the kitchen is
	 * overwhelmed, the driver has gone home — and it must not depend on the rest
	 * of the form being valid.
	 */
	const handleToggleEnabled = useCallback(
		async (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
			setForm((prev) => ({ ...prev, isDeliveryEnabled: checked }));
			if (!isConfigured) {
				// Nothing to toggle server-side yet; it goes with the first save.
				return;
			}
			try {
				await setEnabled({ variables: { locationId, enabled: checked } });
				toast.success(checked ? `${locationName} is now delivering.` : `Delivery paused for ${locationName}.`);
				onSaved?.();
			} catch (toggleError: any) {
				setForm((prev) => ({ ...prev, isDeliveryEnabled: !checked }));
				toast.error(`Could not change this: ${toggleError.message}`);
			}
		},
		[isConfigured, locationId, locationName, onSaved, setEnabled]
	);

	const busy = saving || switching;

	return (
		<Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
			<form onSubmit={handleSubmit}>
				<DialogTitle>
					Delivery settings
					<Typography variant="body2" color="text.secondary">
						{locationName}
					</Typography>
				</DialogTitle>
				<Divider />

				<DialogContent>
					{loadingConfig && !config ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
							<CircularProgress />
						</Box>
					) : (
						<Grid container spacing={3} sx={{ pt: 1 }}>
							<Grid xs={12}>
								<Alert severity={form.isDeliveryEnabled ? 'success' : 'info'} sx={{ alignItems: 'center' }}>
									<FormControlLabel
										sx={{ m: 0 }}
										control={
											<Switch
												checked={form.isDeliveryEnabled}
												onChange={handleToggleEnabled}
												disabled={!canEnable || busy}
											/>
										}
										label={
											form.isDeliveryEnabled
												? 'Customers can order from this location'
												: 'Not accepting delivery orders'
										}
									/>
									<Typography variant="caption" display="block" color="text.secondary">
										{canEnable
											? 'Switching this off hides the location from the app immediately. The settings below are kept.'
											: 'Only a Super Admin can switch delivery on or off. You can still edit the settings below.'}
									</Typography>
								</Alert>
							</Grid>

							<Grid xs={12}>
								<Typography variant="subtitle2">Where the store is</Typography>
								<Typography variant="caption" color="text.secondary">
									Distance to the customer is measured from here, and it decides both who can
									order and what delivery costs. In Google Maps, right-click the store and the
									first two numbers are latitude and longitude.
								</Typography>
							</Grid>
							<Grid xs={12} sm={4}>
								<TextField
									fullWidth
									label="Latitude"
									name="latitude"
									value={form.latitude}
									onChange={handleChange}
									disabled={busy}
									required
									placeholder="12.971600"
									inputProps={{ inputMode: 'decimal' }}
								/>
							</Grid>
							<Grid xs={12} sm={4}>
								<TextField
									fullWidth
									label="Longitude"
									name="longitude"
									value={form.longitude}
									onChange={handleChange}
									disabled={busy}
									required
									placeholder="77.594600"
									inputProps={{ inputMode: 'decimal' }}
								/>
							</Grid>
							<Grid xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
								<Button
									fullWidth
									variant="outlined"
									onClick={useThisDeviceLocation}
									disabled={busy || locating}
								>
									{locating ? 'Locating…' : 'Use this device'}
								</Button>
							</Grid>

							<Grid xs={12} sm={6}>
								<TextField
									fullWidth
									label="Delivery radius"
									name="deliveryRadiusKm"
									value={form.deliveryRadiusKm}
									onChange={handleChange}
									disabled={busy}
									required
									InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
									helperText="Customers further than this are told the store does not deliver to them."
									inputProps={{ inputMode: 'decimal' }}
								/>
							</Grid>
							<Grid xs={12} sm={6}>
								<FormControl fullWidth disabled={busy}>
									<InputLabel>Who delivers</InputLabel>
									<Select
										label="Who delivers"
										value={form.fleetModel}
										onChange={(event) =>
											setForm((prev) => ({ ...prev, fleetModel: event.target.value as FleetModel }))
										}
									>
										<MenuItem value={FleetModel.OwnFleet}>Our own drivers</MenuItem>
										<MenuItem value={FleetModel.Freelance}>Freelance drivers</MenuItem>
									</Select>
									<FormHelperText>How the driver payout below is settled.</FormHelperText>
								</FormControl>
							</Grid>

							<Grid xs={12}>
								<Divider sx={{ mt: 1 }} />
							</Grid>

							<Grid xs={12}>
								<Typography variant="subtitle2">What the customer pays</Typography>
								<Typography variant="caption" color="text.secondary">
									Delivery fee = base + (per km × distance), rounded up, never below the minimum.
								</Typography>
							</Grid>
							<Grid xs={12} sm={4}>
								<TextField
									fullWidth
									label="Base fee"
									name="baseFee"
									value={form.baseFee}
									onChange={handleChange}
									disabled={busy}
									InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
									inputProps={{ inputMode: 'decimal' }}
								/>
							</Grid>
							<Grid xs={12} sm={4}>
								<TextField
									fullWidth
									label="Per km — charged"
									name="perKmRate"
									value={form.perKmRate}
									onChange={handleChange}
									disabled={busy}
									InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
									helperText="Added to the customer's bill"
									inputProps={{ inputMode: 'decimal' }}
								/>
							</Grid>
							<Grid xs={12} sm={4}>
								<TextField
									fullWidth
									label="Minimum fee"
									name="minFee"
									value={form.minFee}
									onChange={handleChange}
									disabled={busy}
									InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
									helperText="The fee never goes below this"
									inputProps={{ inputMode: 'decimal' }}
								/>
							</Grid>

							<Grid xs={12}>
								<Typography variant="subtitle2">What the driver earns</Typography>
								<Typography variant="caption" color="text.secondary">
									A separate rate on purpose: what you charge and what you pay are different
									decisions, and the gap between them is your margin.
								</Typography>
							</Grid>
							<Grid xs={12} sm={4}>
								<TextField
									fullWidth
									label="Per km — paid out"
									name="driverPerKmRate"
									value={form.driverPerKmRate}
									onChange={handleChange}
									disabled={busy}
									required
									InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
									helperText="Paid to the delivery person"
									inputProps={{ inputMode: 'decimal' }}
								/>
							</Grid>

							<Grid xs={12}>
								{error ? (
									<Alert severity="warning">{error}</Alert>
								) : example ? (
									<Alert severity="info" icon={false}>
										<Typography variant="body2">
											<strong>A {SAMPLE_DISTANCE_KM} km delivery:</strong> the customer pays{' '}
											<strong>₹{example.charged}</strong>, the driver earns{' '}
											<strong>₹{example.paid}</strong>, you keep{' '}
											<strong
												style={{ color: example.margin < 0 ? '#B91C1C' : undefined }}
											>
												₹{example.margin}
											</strong>
											{example.margin < 0 ? ' — you are paying more than you charge.' : '.'}
										</Typography>
									</Alert>
								) : null}
							</Grid>

							<Grid xs={12}>
								<Divider sx={{ mt: 1 }} />
							</Grid>

							<Grid xs={12}>
								<DeliveryPayments
									locationId={locationId}
									locationName={locationName}
									isConfigured={isConfigured}
									connection={config?.paymentConnection}
									acceptsCash={form.acceptsCashOnDelivery}
									onCashChange={(accepts) => setForm((prev) => ({ ...prev, acceptsCashOnDelivery: accepts }))}
									disabled={busy}
									onRefresh={() => {
										void refetchConfig();
										onSaved?.();
									}}
								/>
							</Grid>
						</Grid>
					)}
				</DialogContent>

				<Divider />
				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button color="inherit" onClick={onClose} disabled={busy}>
						Cancel
					</Button>
					<Button type="submit" variant="contained" disabled={busy || !!error || loadingConfig}>
						{saving ? 'Saving…' : isConfigured ? 'Save settings' : 'Set up delivery'}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
}
